/**
 * Availability Calendar Component
 * Shows room availability for front desk
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { getRoomTypeColor } from '../../utils/roomTypeColors';

export const AvailabilityCalendar = ({
  availability,
  loading,
  view,
  onViewChange,
  onCellClick
}) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Availability Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!availability) {
    return null;
  }

  return (
    <Card className="border-2 border-gray-200 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Calendar Icon with Tooltip - No Text */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-16 h-16 bg-white/80 border-2 border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:scale-105 hover:border-blue-500 transition-all duration-300 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/ccqkv5bm_avaliablity%20caldendar.png" 
                    alt="Calendar"
                    className="w-full h-full object-contain"
                    style={{ transform: 'scale(1.6)' }}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="px-4 py-2">
                <p className="font-bold text-base">Availability Calendar</p>
                <p className="text-xs text-gray-400 mt-0.5">Room availability overview</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="calendar-view" className="text-sm font-semibold">1 Week</Label>
            <Switch
              id="calendar-view"
              checked={view === 'month'}
              onCheckedChange={(checked) => onViewChange(checked ? 'month' : 'week')}
              data-testid="calendar-view-toggle"
            />
            <Label htmlFor="calendar-view" className="text-sm font-semibold">1 Month</Label>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!availability || Object.keys(availability.availability || {}).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No room types configured for availability calendar
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Room Type</TableHead>
                    {availability.dates?.map((date, idx) => (
                      <TableHead key={idx} className="text-center min-w-[80px]">
                        <div className="text-xs">{date}</div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(availability.availability || {}).map(([roomType, data]) => {
                    const roomColors = getRoomTypeColor(roomType);
                    
                    return (
                    <TableRow key={roomType} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`${roomColors.badge} text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm`}>
                            {roomColors.icon}
                          </span>
                          <span className="font-semibold">{roomType}</span>
                        </div>
                      </TableCell>
                      {data.calendar?.map((day, idx) => {
                        // Use room type color for badge background
                        // Add opacity/transparency based on availability count
                        let opacityClass = '';
                        if (day.available === 0) {
                          opacityClass = 'opacity-30'; // Dim when sold out
                        } else if (day.available <= 2) {
                          opacityClass = 'opacity-60'; // Medium when limited
                        } else {
                          opacityClass = 'opacity-100'; // Full color when many available
                        }
                        
                        return (
                          <TableCell
                            key={idx}
                            className="text-center cursor-pointer hover:bg-accent transition-colors"
                            onClick={() => onCellClick(day.date, roomType, roomType)}
                            data-testid={`calendar-cell-${roomType}-${idx}`}
                          >
                            <Badge
                              className={`${roomColors.badge} text-white font-bold shadow-sm ${opacityClass} hover:opacity-100 transition-opacity`}
                            >
                              {day.available}
                            </Badge>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
              <span className="font-semibold text-gray-700">Availability Status:</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-black opacity-100"></div>
                <span>High (3+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-black opacity-60"></div>
                <span>Limited (1-2)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-black opacity-30"></div>
                <span>Sold out (0)</span>
              </div>
              <span className="ml-4 text-gray-500 italic">* Each room type has its own color</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
